import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Conversation, Message } from '../../hooks/useChat';
import { latestClientContext, riderLabel, systemLabel } from '../../hooks/chat/mappers';
import { useChatTyping } from '../../hooks/chat/useChatTyping';
import { useSessionHardwareFamily } from '../../hooks/chat/useSessionHardwareFamily';
import { downloadBlob } from '../../utils';
import CustomToast from '../../components/Redesing/custom-toast';
import { ActionPanel } from './ActionPanel';
import { Composer, ComposerDrafts, SendOptions } from './Composer';
import { EndConversationDialog } from './EndConversationDialog';
import { sessionLink } from './sessionLink';
import { MessageThread } from './MessageThread';
import { TypingIndicator } from './TypingIndicator';
import { formatDateTime, statusLabel } from './format';
import { csatTone } from './metricsFormat';
import { formatDuration } from './attachments';
import { SHOW_ASSISTANT } from './flags';
import { ChatTagGlossary } from './tagGlossary';
import { holderLabel, isHeldByOther } from './assignee';

/**
 * A bucket photo or video in the transcript. Its signed URL is left out: it
 * expires within the hour, and a transcript is kept for longer.
 */
function mediaNote(m: Message): string | null {
  if (!m.media) return null;
  if (m.media.kind === 'photo') return '(photo)';
  return m.media.duration_ms
    ? `(video ${formatDuration(m.media.duration_ms)})`
    : '(video)';
}

interface Props {
  conversation: Conversation;
  /** Undefined until the thread has loaded. */
  messages: Message[] | undefined;
  onSendMessage: (content: string, options: SendOptions) => Promise<void>;
  /**
   * Closes the session in ant with the note and the tags the End dialog
   * added; rejects (after saying why) to keep the dialog open.
   */
  onEndConversation: (resolutionNote: string, tags: string[]) => Promise<void>;
  myAdminId: string | null;
  adminEmail: string;
  /** The signed-in agent's name, for {{agent_first_name}} in saved replies. */
  adminName?: string;
  /** A Movatic admin: may write saved replies for every system. */
  canManageGlobalReplies?: boolean;
  /** Opens another session of the rider (from the panel's history). */
  onOpenConversation?: (conversation: Conversation) => void;
  /** Back to the queue, on screens too narrow to show both. */
  onBack?: () => void;
  onAssign: (convId: string, adminId: string | null) => Promise<void>;
  /** One glossary tag on or off the session; each rejects with ant's refusal. */
  onAddTag: (convId: string, tag: string) => Promise<void>;
  onRemoveTag: (convId: string, tag: string) => Promise<void>;
  onSetPriority: (convId: string, priority: 'normal' | 'urgent') => Promise<void>;
  /** CS's glossary, for the tag pickers; null while it loads or when it could not be read. */
  glossary?: ChatTagGlossary | null;
  glossaryError?: string | null;
  /** Posts an internal note (a transfer's, a support ticket's). */
  onAddNote?: (convId: string, note: string) => Promise<void>;
  /** Takes an assistant session over; rejects (after saying why) when ant refuses. */
  onTakeOver?: (convId: string) => Promise<void>;
  /**
   * Takes over a chat another agent has, named as the view showed them (null
   * when ant did not say); rejects (after saying why) when ant refuses.
   */
  onTakeOverFromAgent?: (convId: string, fromName: string | null) => Promise<void>;
  /** The page's drafts of the other conversations, for the composer (useComposerDrafts). */
  drafts?: ComposerDrafts;
  /**
   * Counts the page's asks to end this conversation (its `e` key): each new
   * count opens the End conversation dialog, as the header icon does.
   */
  endRequested?: number;
  /**
   * ant refused this admin's writes on the session (a role that reads the
   * chat and no more, such as Support Viewer): the composer, notes, assign,
   * tags, priority and End conversation go, and a notice says so. It wins
   * over another agent having the chat: there is nothing to take over.
   */
  writeRefused?: boolean;
}

/** Why and when the assistant handed the chat to a person. */
export function EscalationNotice({ conversation }: { conversation: Conversation }) {
  if (
    !SHOW_ASSISTANT ||
    !conversation.escalation_reason ||
    conversation.handled_by === 'assistant'
  ) {
    return null;
  }
  const at = conversation.escalated_at ? formatDateTime(conversation.escalated_at) : null;
  return (
    <Alert
      severity="warning"
      icon={<SmartToyOutlinedIcon />}
      sx={{ borderRadius: 0, py: 0.25 }}
      data-testid="escalation-notice"
    >
      The Assistant handed this chat over{at ? ` at ${at}` : ''}:{' '}
      {conversation.escalation_reason}
    </Alert>
  );
}

/** In place of the composer on an assistant session: read-only until taken over. */
export function AssistantNotice({ onTakeOver }: { onTakeOver?: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}
      data-testid="assistant-notice"
    >
      <SmartToyOutlinedIcon color="secondary" />
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
        {SHOW_ASSISTANT
          ? 'The Assistant is answering this rider. Take over to reply yourself; the chat is assigned to you and stays with a person from then on.'
          : 'The Assistant is answering this rider. This chat is read-only.'}
      </Typography>
      {SHOW_ASSISTANT && onTakeOver && (
        <Button
          variant="contained"
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          onClick={async () => {
            setBusy(true);
            try {
              await onTakeOver();
            } catch (_) {
              // The page has said why.
            } finally {
              setBusy(false);
            }
          }}
        >
          Take over
        </Button>
      )}
    </Stack>
  );
}

/** In place of the composer once ant refused this admin's writes on the session. */
export function ReadOnlyNotice() {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}
      data-testid="read-only-notice"
    >
      <VisibilityOutlinedIcon fontSize="small" color="disabled" />
      <Typography variant="body2" color="text.secondary">
        You can read this chat but not reply.
      </Typography>
    </Stack>
  );
}

/** How a closed session ended, shown at the end of its thread. */
export function ClosedBanner({ conversation }: { conversation: Conversation }) {
  const by = conversation.closed_by_admin_name;
  const at = conversation.closed_at ? formatDateTime(conversation.closed_at) : null;
  return (
    <Alert
      severity="info"
      icon={<TaskAltIcon />}
      sx={{ alignSelf: 'center', width: '100%', maxWidth: 560, mt: 1 }}
      data-testid="closed-banner"
    >
      <AlertTitle sx={{ mb: conversation.resolution_note ? 0.5 : 0 }}>
        {`Ended${by ? ` by ${by}` : ''}${at ? ` at ${at}` : ''}`}
      </AlertTitle>
      {conversation.resolution_note && (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          Note: {conversation.resolution_note}
        </Typography>
      )}
    </Alert>
  );
}

/** The rider's rating of a closed session, in the thread header. */
export function CsatSummary({ conversation }: { conversation: Conversation }) {
  const score = conversation.csat_score;
  if (score === null || score === undefined) {
    return (
      <Tooltip title="The rider has not rated this chat">
        <Chip size="small" variant="outlined" label="Not rated" data-testid="csat" />
      </Tooltip>
    );
  }
  const comment = conversation.csat_comment?.trim();
  const at = conversation.csat_at ? formatDateTime(conversation.csat_at) : null;
  return (
    <Tooltip
      title={
        <>
          {comment ? `“${comment}”` : 'No comment'}
          {at ? (
            <Box component="span" sx={{ display: 'block', opacity: 0.8 }}>
              Rated {at}
            </Box>
          ) : null}
        </>
      }
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={0.75}
        sx={{ minWidth: 0, maxWidth: 280 }}
        data-testid="csat"
      >
        <Chip
          size="small"
          icon={<StarRoundedIcon />}
          label={`${score}/5`}
          color={csatTone(score)}
          aria-label={`Rated ${score} out of 5`}
        />
        {comment && (
          <Typography variant="caption" color="text.secondary" noWrap>
            “{comment}”
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}

export function ConversationView({
  conversation,
  messages: loadedMessages,
  onSendMessage,
  onEndConversation,
  myAdminId,
  adminEmail,
  adminName = '',
  canManageGlobalReplies = false,
  onOpenConversation,
  onBack,
  onAssign,
  onAddTag,
  onRemoveTag,
  onSetPriority,
  glossary = null,
  glossaryError = null,
  onAddNote,
  onTakeOver,
  onTakeOverFromAgent,
  drafts,
  endRequested = 0,
  writeRefused = false,
}: Props) {
  const [endOpen, setEndOpen] = useState(false);
  // The session's rental family, read once for this session: the tag
  // pickers narrow to it. The queue never loads it.
  const hardwareFamily = useSessionHardwareFamily(conversation.id);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const closed = conversation.status === 'closed';
  // The assistant's session: watched, not answered, until an agent takes it over.
  const assistant = conversation.handled_by === 'assistant' && !closed;
  // This admin reads the session and no more: the same as watching, without
  // the Take over. Nothing to end, so a dialog open when the refusal came goes.
  const readOnly = writeRefused && !closed && !assistant;
  // Another agent has the chat (assignee.ts): notes, yes; the reply and the
  // end wait for a Take over. A read-only admin has nothing to take over.
  const heldByOther = !readOnly && isHeldByOther(conversation, myAdminId);
  const messages = loadedMessages ?? [];
  const lastMessage = messages.length ? messages[messages.length - 1] : null;
  // Nobody types into a closed session: nothing to subscribe to or send.
  const typing = useChatTyping(
    closed ? null : conversation.conversation_id,
    myAdminId,
    lastMessage
  );
  const clientContext = latestClientContext(loadedMessages);

  // Closed elsewhere (another agent, the rider) while the dialog was open,
  // ant refused this admin's writes, or the chat went to another agent:
  // there is nothing left for this admin to end.
  useEffect(() => {
    if (closed || readOnly || heldByOther) setEndOpen(false);
  }, [closed, readOnly, heldByOther]);
  // The page's `e`: the same dialog as the header icon, when there is one.
  // The count is the page's and outlives this view (one is made per chat):
  // the count a view mounts with was an earlier chat's ask, not this one's.
  const endHandledRef = useRef(endRequested);
  useEffect(() => {
    if (endRequested === endHandledRef.current) return;
    endHandledRef.current = endRequested;
    if (!closed && !assistant && !readOnly && !heldByOther) setEndOpen(true);
    // Only a new ask opens it; the session closing is handled above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endRequested]);

  const handleExport = () => {
    const lines = [
      `Conversation with ${riderLabel(conversation)}`,
      `Phone: ${conversation.user_phone}`,
      `System: ${systemLabel(conversation)}`,
      `Status: ${statusLabel(conversation.status)}`,
      `Opened: ${formatDateTime(conversation.created_on)}`,
      ...(conversation.closed_at
        ? [`Ended: ${formatDateTime(conversation.closed_at)}`]
        : []),
      ...(conversation.resolution_note
        ? [`Resolution note: ${conversation.resolution_note}`]
        : []),
      '-'.repeat(50),
      ...messages.map((m) => {
        const prefix =
          m.sender_type === 'note'
            ? `[Internal note, ${m.sender_name}]`
            : `[${m.sender_name}]`;
        const text = [m.content, m.image_url && `(photo: ${m.image_url})`, mediaNote(m)]
          .filter(Boolean)
          .join(' ');
        return `${formatDateTime(m.created_on)}  ${prefix} ${text}`;
      }),
    ];
    downloadBlob(
      new Blob([lines.join('\n')], { type: 'text/plain' }),
      `chat-${conversation.user_phone || 'rider'}-${conversation.id.slice(0, 8)}.txt`
    );
  };

  /** The link that opens this conversation here, for a teammate or a ticket. */
  const copyLink = async () => {
    const link = sessionLink(conversation.id);
    try {
      if (!navigator.clipboard) throw new Error('No clipboard');
      await navigator.clipboard.writeText(link);
      CustomToast({ type: 'success', message: 'Link copied' });
    } catch {
      CustomToast({
        type: 'error',
        message: `Could not copy the link. Copy it from here: ${link}`,
        duration: 10000,
      });
    }
  };

  const query = searchQuery.trim().toLowerCase();
  const visibleMessages = query
    ? messages.filter((m) => m.content.toLowerCase().includes(query))
    : messages;

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          {onBack && (
            <IconButton
              aria-label="Back to the queue"
              onClick={onBack}
              sx={{ display: { md: 'none' } }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
            {riderLabel(conversation)[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>
              {riderLabel(conversation)}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {conversation.user_phone}
              {' · '}
              {systemLabel(conversation)}
            </Typography>
          </Box>
          {SHOW_ASSISTANT && assistant && (
            <Chip
              icon={<SmartToyOutlinedIcon />}
              label="Assistant"
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
          {closed && <CsatSummary conversation={conversation} />}
          {closed && (
            <Chip
              icon={<LockOutlinedIcon />}
              label="Closed"
              size="small"
              variant="outlined"
            />
          )}
          <Tooltip title="Search messages">
            <IconButton
              aria-label="Search messages"
              color={showSearch ? 'secondary' : 'default'}
              onClick={() => {
                setShowSearch((s) => !s);
                setSearchQuery('');
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy link">
            <IconButton aria-label="Copy link" onClick={copyLink}>
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download transcript">
            <span>
              <IconButton
                aria-label="Download transcript"
                onClick={handleExport}
                disabled={messages.length === 0}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {!closed && !assistant && !readOnly && !heldByOther && (
            <Tooltip title="End conversation">
              <IconButton
                aria-label="End conversation"
                color="error"
                onClick={() => setEndOpen(true)}
              >
                <TaskAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {showSearch && (
          <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              autoFocus
              size="small"
              fullWidth
              placeholder="Search this conversation"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  setShowSearch(false);
                  setSearchQuery('');
                }
              }}
              inputProps={{ 'aria-label': 'Search this conversation' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText={
                query
                  ? `${visibleMessages.length} result${
                      visibleMessages.length === 1 ? '' : 's'
                    }`
                  : undefined
              }
            />
          </Box>
        )}

        <EscalationNotice conversation={conversation} />

        <MessageThread
          threadKey={conversation.id}
          filterKey={query}
          myAdminId={myAdminId}
          messages={loadedMessages}
          visibleMessages={visibleMessages}
          riderLastRead={conversation.last_read_at_user}
          emptyText={query ? 'No message matches your search' : 'No messages yet'}
          footer={closed ? <ClosedBanner conversation={conversation} /> : null}
        />

        <TypingIndicator rider={typing.rider} agents={typing.agents} />

        {assistant ? (
          <AssistantNotice
            onTakeOver={onTakeOver ? () => onTakeOver(conversation.id) : undefined}
          />
        ) : readOnly ? (
          <ReadOnlyNotice />
        ) : closed ? (
          <Box
            sx={{ px: 2, py: 2, borderTop: 1, borderColor: 'divider' }}
            data-testid="closed-notice"
          >
            <Typography variant="body2" color="text.secondary" align="center">
              This conversation is closed. The rider&apos;s next message opens a new one.
            </Typography>
          </Box>
        ) : (
          <Composer
            conversationId={conversation.id}
            drafts={drafts}
            onSend={onSendMessage}
            anchoredRentalId={conversation.context_rental_id}
            riderId={conversation.user_id}
            onTyping={typing.notifyTyping}
            systemId={conversation.system_id}
            systemName={conversation.system_name}
            riderName={conversation.user_name}
            agentName={adminName}
            canManageGlobal={canManageGlobalReplies}
            heldBy={heldByOther ? holderLabel(conversation.assigned_admin_name) : null}
            onTakeOver={
              heldByOther && onTakeOverFromAgent
                ? () =>
                    onTakeOverFromAgent(conversation.id, conversation.assigned_admin_name)
                : undefined
            }
          />
        )}
      </Box>

      <ActionPanel
        conversation={conversation}
        clientContext={clientContext}
        myAdminId={myAdminId}
        adminEmail={adminEmail}
        onOpenConversation={onOpenConversation}
        onEndConversation={() => setEndOpen(true)}
        assignConversation={onAssign}
        onAddTag={onAddTag}
        onRemoveTag={onRemoveTag}
        onSetPriority={onSetPriority}
        glossary={glossary}
        glossaryError={glossaryError}
        hardwareFamily={hardwareFamily}
        messages={messages}
        adminName={adminName}
        onAddNote={onAddNote}
        readOnly={assistant}
        writeRefused={readOnly}
        heldByOther={heldByOther}
      />

      <EndConversationDialog
        open={endOpen && !closed && !readOnly && !heldByOther}
        riderName={riderLabel(conversation)}
        tags={conversation.tags ?? []}
        glossary={glossary}
        glossaryError={glossaryError}
        hardwareFamily={hardwareFamily}
        onRemoveTag={(tag) => onRemoveTag(conversation.id, tag)}
        onCancel={() => setEndOpen(false)}
        onConfirm={async (note, tags) => {
          await onEndConversation(note, tags);
          setEndOpen(false);
        }}
      />
    </Box>
  );
}
